import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Crown, ArrowsClockwise, Wallet, CalendarBlank, Timer, Sparkle } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminCurrentVipsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) navigate("/");
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/current-vips");
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load current VIPs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_super_admin) load();
  }, [user, load]);

  if (authLoading || !user?.is_super_admin) {
    return <PageShell><Header /><div className="p-10 text-center text-muted">Loading…</div><Footer /></PageShell>;
  }

  return (
    <PageShell>
      <Header />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="current-vips-page">
        <header className="mb-8">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <Crown size={14} weight="duotone" /> Super-admin · VIP King subscribers
          </div>
          <div className="flex items-baseline justify-between gap-4 flex-wrap mt-3">
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
              Current <span className="text-gold">VIPs</span>
            </h1>
            <button
              onClick={load}
              className="btn-ghost rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 hover:text-gold"
              data-testid="refresh-btn"
            >
              <ArrowsClockwise size={14} weight="bold" /> Refresh
            </button>
          </div>
          <p className="text-muted text-sm mt-3 font-light">
            Anyone with an active unlock-all window or VIP bonus credit left. Soonest expiry first.
          </p>
        </header>

        {error && (
          <div className="rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm mb-6" data-testid="vips-error">{error}</div>
        )}

        {loading && !data && <div className="p-10 text-center text-muted text-sm">Loading VIPs…</div>}

        {data && (
          <>
            <div className="mb-4 text-sm text-muted" data-testid="vips-count">
              <span className="font-serif-display text-3xl text-[#3F2D1B] font-light">{data.total}</span>
              <span className="ml-2">active {data.total === 1 ? "VIP" : "VIPs"} · as of {new Date(data.as_of).toLocaleString("en-IN")}</span>
            </div>

            {data.vips.length === 0 ? (
              <div className="rounded-2xl border border-soft p-10 text-center text-muted text-sm" data-testid="vips-empty">
                No active VIPs right now.
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden border border-soft" data-testid="vips-table">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF4E8] text-xs text-muted">
                    <tr>
                      <th className="text-left p-3">VIP</th>
                      <th className="text-left p-3 hidden sm:table-cell"><span className="inline-flex items-center gap-1"><CalendarBlank size={12} /> Taken on</span></th>
                      <th className="text-left p-3"><span className="inline-flex items-center gap-1"><Timer size={12} /> Expires</span></th>
                      <th className="text-left p-3 hidden md:table-cell"><span className="inline-flex items-center gap-1"><Wallet size={12} /> Credit left</span></th>
                      <th className="text-left p-3">Unlocked now</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-soft bg-white">
                    {data.vips.map((v, i) => (
                      <tr key={v.user_id} className="hover:bg-amber-50/40 align-top" data-testid={`vip-row-${i}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2.5">
                            {v.picture ? (
                              <img src={v.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-900 inline-flex items-center justify-center">
                                <Crown size={14} weight="fill" />
                              </span>
                            )}
                            <div className="min-w-0">
                              <div className="font-medium text-[#3F2D1B] truncate">{v.name}</div>
                              <div className="text-xs text-muted truncate">{v.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-muted hidden sm:table-cell" data-testid={`vip-since-${i}`}>{fmtDate(v.vip_since)}</td>
                        <td className="p-3" data-testid={`vip-expires-${i}`}>
                          <div>{fmtDate(v.expires_at)}</div>
                          {v.window_active ? (
                            <div className={`text-xs mt-0.5 font-medium ${v.days_left <= 3 ? "text-rose-700" : "text-emerald-700"}`}>
                              {v.days_left} {v.days_left === 1 ? "day" : "days"} left
                            </div>
                          ) : (
                            <div className="text-xs mt-0.5 text-muted">window ended · credit remains</div>
                          )}
                        </td>
                        <td className="p-3 hidden md:table-cell" data-testid={`vip-credit-${i}`}>
                          <span className="font-medium text-[#3F2D1B]">₹{v.vip_credit_remaining.toLocaleString("en-IN")}</span>
                        </td>
                        <td className="p-3" data-testid={`vip-unlocked-${i}`}>
                          <div className="flex flex-wrap gap-1.5 max-w-md">
                            {v.window_active && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-xs font-medium">
                                <Sparkle size={11} weight="fill" /> All premium tools · VIP active
                              </span>
                            )}
                            {v.tools_unlocked.map((t) => (
                              <span key={t} className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 text-xs">{t}</span>
                            ))}
                            {!v.window_active && v.tools_unlocked.length === 0 && (
                              <span className="text-xs text-muted">No individual tool unlocks</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </PageShell>
  );
}
